export function getTableRowCount(fieldVal: unknown): number {
  if (!fieldVal) return 0
  if (
    typeof fieldVal === 'object' &&
    !Array.isArray(fieldVal) &&
    (fieldVal as { type?: string }).type === 'Array' &&
    Array.isArray((fieldVal as { value?: unknown }).value)
  ) {
    return (fieldVal as { value: unknown[] }).value.length
  }
  const rows =
    (fieldVal as { rows?: unknown[] }).rows ??
    (fieldVal as { value?: { rows?: unknown[] } }).value?.rows
  if (Array.isArray(rows)) return rows.length
  if (Array.isArray(fieldVal)) return fieldVal.length
  return 0
}

/** Сервер явно вернул пустую таблицу (reload/search/filter с 0 строк). */
function isExplicitEmptyTableField(fieldVal: unknown): boolean {
  if (!fieldVal || typeof fieldVal !== 'object' || Array.isArray(fieldVal)) return false
  const entry = fieldVal as { type?: string; value?: unknown }
  if (String(entry.type ?? '') !== 'UniDataTable') return false
  const val = entry.value
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false
  return (val as { empty?: unknown }).empty === true
}

/**
 * TODO: защита от обнуления. Работает, возможно, некорректно, добавлен фикс isExplicitEmptyTableField
 * Вероятно, логика должна быть такая: если элемента нет - не трогаем, если есть - в любом случае заменяем,
 * даже если он пустой. Проверить - есть ли где0то кейсы, когда данные приходят некорректно пустые!
 *
 * @param oldFields
 * @param newFields
 */
export function mergeFieldsDefensive(
  oldFields: Record<string, unknown>,
  newFields: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...oldFields, ...newFields }
  for (const key of Object.keys(newFields)) {
    const oldVal = oldFields[key]
    const newVal = newFields[key]
    // Reload нормализует empty:true|false — всегда авторитетная замена таблицы.
    if (isAuthoritativeUniDataTable(newVal)) {
      merged[key] = newVal
      continue
    }
    const oldCount = getTableRowCount(oldVal)
    const newCount = getTableRowCount(newVal)
    if (oldCount > 0 && newCount === 0) {
      // Авторитетный empty:true (executeScript/reloadElement с is_refill) должен очищать таблицу.
      if (!isExplicitEmptyTableField(newVal)) {
        merged[key] = oldVal
      }
    }
  }
  return merged
}

/** UniDataTable с явным boolean empty (после normalizeReloadTableEmptyFlag). */
export function isAuthoritativeUniDataTable(fieldVal: unknown): boolean {
  if (!fieldVal || typeof fieldVal !== 'object' || Array.isArray(fieldVal)) return false
  const entry = fieldVal as { type?: string; value?: unknown }
  if (String(entry.type ?? '') !== 'UniDataTable') return false
  const val = entry.value
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false
  return typeof (val as { empty?: unknown }).empty === 'boolean'
}

/** New platform session in the same slot — incoming snapshot replaces prior session fields. */
export function isDistinctPlatformSessionChange(
  oldSessionId: unknown,
  newSessionId: unknown,
  options?: { isAdjacentWindow?: boolean },
): boolean {
  if (options?.isAdjacentWindow) return false
  const oldSid = oldSessionId != null ? String(oldSessionId).trim() : ''
  const newSid = newSessionId != null ? String(newSessionId).trim() : ''
  return Boolean(oldSid && newSid && oldSid !== newSid)
}

export function mergePageDataFieldsForSetPageData(
  oldFields: Record<string, unknown>,
  newFields: Record<string, unknown>,
  sessionChanged: boolean,
): Record<string, unknown> {
  if (sessionChanged) {
    return mergeFieldsDefensive({}, newFields)
  }
  return mergeFieldsDefensive(oldFields, newFields)
}

/** true, если число строк любой UniDataTable отличается */
export function pageDataTableRowCountsDiffer(
  oldData: Record<string, any> | undefined | null,
  newData: Record<string, any> | undefined | null,
): boolean {
  const layers: Array<[Record<string, unknown>, Record<string, unknown>]> = [
    [
      (oldData?.uniData?.fields?.fields as Record<string, unknown>) || {},
      (newData?.uniData?.fields?.fields as Record<string, unknown>) || {},
    ],
    [
      (oldData?.jsResult?.changed_data?.fields?.fields as Record<string, unknown>) || {},
      (newData?.jsResult?.changed_data?.fields?.fields as Record<string, unknown>) || {},
    ],
  ]
  for (const [oldFields, newFields] of layers) {
    const keys = new Set([...Object.keys(oldFields), ...Object.keys(newFields)])
    for (const key of keys) {
      if (getTableRowCount(oldFields[key]) !== getTableRowCount(newFields[key])) {
        return true
      }
    }
  }
  return false
}
